import { DataTexture, FloatType, NearestFilter, RedFormat } from 'three/webgpu';
import { float, fract } from 'three/tsl';
import type { Node } from 'three/webgpu';
import { squareOff } from './sdf';
import { FFT_SIZE } from '../../audio/analyser';

type F = Node<'float'>;

/** Shapes a sample by its position through the buffer. `t` runs 0-1. */
export type Ramp = (value: F, t: F) => F;

/** Envelope shapes lifted from the original 2D display, expressed as node math. */
export const RAMPS: {
  normal: Ramp;
  loop: Ramp;
  pingpong: Ramp;
  reverse: Ramp;
  fuzz: Ramp;
  echo: Ramp;
} = {
  /** Decays to silence — the plain one-shot. */
  normal: (v, t) => v.mul(t.oneMinus()),
  /** Two decaying halves, as a loop restarting midway. */
  loop: (v, t) => v.mul(fract(t.mul(2)).oneMinus()),
  /** Decays twice as fast, then mirrors below the axis. */
  pingpong: (v, t) => v.mul(t.mul(2).oneMinus()),
  /** Swells instead of decaying. */
  reverse: (v, t) => v.mul(t),
  /** Hard-clipped square, ignoring position. */
  fuzz: (v) => squareOff(v, float(0.25), float(0.5)),
  /** A looping envelope under an overall decay. */
  echo: (v, t) => RAMPS.normal(RAMPS.loop(v, t), t),
};

export type RampName = keyof typeof RAMPS;

/** Live time-domain samples from the synth, one texel per sample. */
export function createWaveformTexture(length = FFT_SIZE): DataTexture {
  const texture = new DataTexture(new Float32Array(length), length, 1, RedFormat, FloatType);
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

/** The fixed low-pass curve the FILTER panel draws. */
export const FILTER_CURVE = [
  0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.52, 0.55, 0.6, 0.7, 0.8, 0.9, 1,
];

export function createCurveTexture(values: number[]): DataTexture {
  const texture = new DataTexture(new Float32Array(values), values.length, 1, RedFormat, FloatType);
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}
